import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { getUser, isTabScopedSessionRole } from '@/utilities/setting';

/**
 * UI filter state (date ranges, selects, tabs, search, sorting, page) that survives
 * a page refresh and route changes, per logged-in user.
 *
 * - Stored like the login itself: localStorage for most roles, sessionStorage for the
 *   tab-scoped roles (agent / team lead), so filters live exactly as long as the session.
 * - Keys are `obd3:filters:<userId>:<key>`. They are removed on logout, switch and
 *   switch back through `clearUserScopedCaches()` (lib/auth/strategy.ts), and by
 *   `Storage.clearAll()` on the other logout paths.
 */
export const PERSISTED_FILTERS_PREFIX = 'obd3:filters:';

interface CurrentUser {
  id?: number | string | null;
  role?: string | null;
}

function scope(): { store: Storage; prefix: string } | null {
  try {
    const user = (getUser() || null) as CurrentUser | null;
    const uid = user?.id ?? 'anon';
    const store = isTabScopedSessionRole(user?.role) ? sessionStorage : localStorage;
    return { store, prefix: `${PERSISTED_FILTERS_PREFIX}${uid}:` };
  } catch {
    return null;
  }
}

/* Dates are kept as Dates: JSON alone would turn them into strings. */
const DATE_TAG = '__date__';

function serialize(value: unknown): string {
  return JSON.stringify(value, function replacer(this: Record<string, unknown>, k: string, v: unknown) {
    const raw = this[k];
    return raw instanceof Date ? { [DATE_TAG]: Number.isNaN(raw.getTime()) ? null : raw.toISOString() } : v;
  });
}

function deserialize<T>(text: string): T {
  return JSON.parse(text, (_k, v: unknown) => {
    if (v && typeof v === 'object' && !Array.isArray(v) && DATE_TAG in (v as Record<string, unknown>)) {
      const iso = (v as Record<string, unknown>)[DATE_TAG];
      return typeof iso === 'string' ? new Date(iso) : null;
    }
    return v;
  }) as T;
}

/** Saved value, or undefined when there is none (or it cannot be read). */
export function readPersisted<T>(key: string): T | undefined {
  const s = scope();
  if (!s) return undefined;
  try {
    const raw = s.store.getItem(s.prefix + key);
    return raw === null ? undefined : deserialize<T>(raw);
  } catch {
    return undefined;
  }
}

export function writePersisted(key: string, value: unknown): void {
  const s = scope();
  if (!s) return;
  try {
    s.store.setItem(s.prefix + key, serialize(value));
  } catch {
    /* storage full or unavailable: keep working without persistence */
  }
}

export function removePersisted(key: string): void {
  const s = scope();
  if (!s) return;
  try {
    s.store.removeItem(s.prefix + key);
  } catch {
    /* no-op */
  }
}

/** Removes every saved filter of every user (both storages). */
export function clearPersistedFilters(): void {
  try {
    [localStorage, sessionStorage].forEach((store) => {
      const doomed: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k && k.startsWith(PERSISTED_FILTERS_PREFIX)) doomed.push(k);
      }
      doomed.forEach((k) => store.removeItem(k));
    });
  } catch {
    /* no-op */
  }
}

/**
 * `useState` that is saved under `key` and restored on the next mount.
 * Pass `null` as the key to behave exactly like `useState` (nothing saved).
 * Use a stable, page-unique key such as `cdr-report:startDate`.
 */
export function usePersistedState<T>(
  key: string | null,
  initial: T | (() => T),
  options?: {
    /** Adjust a restored value, e.g. put a date back to today while keeping the other filters. */
    onRestore?: (saved: T) => T;
  },
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (key) {
      const saved = readPersisted<T>(key);
      if (saved !== undefined) return options?.onRestore ? options.onRestore(saved) : saved;
    }
    return typeof initial === 'function' ? (initial as () => T)() : initial;
  });

  useEffect(() => {
    if (key) writePersisted(key, value);
  }, [key, value]);

  return [value, setValue];
}

/**
 * A page's filters as one saved object:
 *   const { filters, setFilter, setFilters, reset } = usePersistedFilters('cdr-report', { from, to, did: '' });
 * Saved keys missing from `defaults` are dropped; new default keys are filled in.
 */
export function usePersistedFilters<T extends Record<string, unknown>>(key: string, defaults: T | (() => T)) {
  const [filters, setFilters] = useState<T>(() => {
    const base = typeof defaults === 'function' ? (defaults as () => T)() : defaults;
    const saved = readPersisted<Partial<T>>(key);
    if (!saved || typeof saved !== 'object') return base;
    const merged = { ...base };
    (Object.keys(base) as (keyof T)[]).forEach((k) => {
      if (k in saved) merged[k] = saved[k] as T[keyof T];
    });
    return merged;
  });

  useEffect(() => {
    writePersisted(key, filters);
  }, [key, filters]);

  const setFilter = <K extends keyof T>(name: K, value: T[K]) => setFilters((prev) => ({ ...prev, [name]: value }));
  const reset = () => setFilters(typeof defaults === 'function' ? (defaults as () => T)() : defaults);

  return { filters, setFilter, setFilters, reset };
}
